import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  TableSortLabel,
} from '@mui/material'
import { Edit as EditIcon, Search as SearchIcon, Link as LinkIcon } from '@mui/icons-material'
import { User } from '../../types/auth'
import { NivelAcesso } from '../../types/auth'
import { UserSortField } from '../../api/usuarios'
import dayjs from 'dayjs'

interface UsuariosTableProps {
  usuarios: User[]
  onEdit: (usuario: User) => void
  onLinkEmpresas: (usuario: User) => void
  loading?: boolean
  sortField: UserSortField
  sortDirection: 'asc' | 'desc'
  onSort: (field: UserSortField) => void
}

const UsuariosTable: React.FC<UsuariosTableProps> = ({
  usuarios = [],
  onEdit,
  onLinkEmpresas,
  loading,
  sortField,
  sortDirection,
  onSort,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')

  const safeUsuarios = Array.isArray(usuarios) ? usuarios : []

  // Filtrar usuários por termo de busca
  const filteredUsuarios = useMemo(
    () =>
      safeUsuarios.filter(
        (usuario) =>
          usuario.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [safeUsuarios, searchTerm]
  )

  const sortableColumns: Array<{ key: UserSortField; label: string; align?: 'left' | 'right' }> = [
    { key: 'id', label: t('common.id'), align: 'right' },
    { key: 'name', label: t('users.name') },
    { key: 'email', label: t('users.email') },
    { key: 'nivel_acesso', label: t('users.accessLevel') },
    { key: 'is_active', label: t('common.status') },
    { key: 'created_at', label: t('common.created_at') },
  ]

  // Paginação
  const paginatedUsuarios = filteredUsuarios.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSort = (field: UserSortField) => {
    onSort(field)
    setPage(0)
  }

  const getSortDirection = (field: UserSortField) => {
    if (sortField !== field) return 'asc'
    return sortDirection
  }

  const getNivelAcessoLabel = (nivel: number): string => {
    switch (nivel) {
      case NivelAcesso.SUPER_ADMIN:
        return t('accessLevels.superAdmin')
      case NivelAcesso.ADMINISTRADOR:
        return t('accessLevels.administrator')
      case NivelAcesso.GERENTE:
        return t('accessLevels.manager')
      case NivelAcesso.SUPERVISOR:
        return t('accessLevels.supervisor')
      case NivelAcesso.OPERADOR:
        return t('accessLevels.operator')
      case NivelAcesso.USUARIO_PADRAO:
        return t('accessLevels.defaultUser')
      default:
        return `${t('users.accessLevel')} ${nivel}`
    }
  }

  const getNivelAcessoColor = (nivel: number): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (nivel) {
      case NivelAcesso.SUPER_ADMIN:
        return 'error'
      case NivelAcesso.ADMINISTRADOR:
        return 'primary'
      case NivelAcesso.GERENTE:
        return 'info'
      default:
        return 'default'
    }
  }

  if (loading) {
    return (
      <Box p={3}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    )
  }

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box p={2}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t('users.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            '& .MuiInputBase-root': { minHeight: 28 },
            '& .MuiOutlinedInput-input': { py: 0.75 },
          }}
        />
      </Box>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortableColumns.map((column) => {
                const isActive = sortField === column.key
                return (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    sortDirection={isActive ? sortDirection : false}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                )
              })}
              <TableCell>{t('users.superAdminDescription') || 'Descrição Super Admin'}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" py={3}>
                    {searchTerm ? t('users.noResults') : t('users.noData')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsuarios.map((usuario) => (
                <TableRow key={usuario.id} hover>
                  <TableCell>{usuario.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {usuario.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {usuario.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getNivelAcessoLabel(usuario.nivel_acesso)}
                      color={getNivelAcessoColor(usuario.nivel_acesso)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={usuario.is_active ? t('common.active') : t('common.inactive')}
                      color={usuario.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {usuario.created_at ? (
                      <Typography variant="body2" color="text.secondary">
                        {dayjs(usuario.created_at).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {usuario.descricao_superadmin || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(usuario)}
                      color="primary"
                      aria-label={t('common.edit')}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onLinkEmpresas(usuario)}
                      color="secondary"
                      aria-label={t('users.linkCompanies')}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredUsuarios.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Paper>
  )
}

export default UsuariosTable

